import React, { useCallback, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { withRouter, RouteComponentProps } from 'react-router-dom'
import { CloseIcon, HelpIcon } from '@astrid/components'
import GlobalScore from 'components/GlobalScore/GlobalScore'
import Level from 'components/Level/Level'
import ExerciseProgressBar from 'components/ExerciseProgressBar/ExerciseProgressBar'
import { ReactComponent as AchievementIcon } from 'resources/graphics/achievements-icons/onboarding-5.svg'
import { ReactComponent as MenuIcon } from 'resources/graphics/icons/menu.svg'
import { ReactComponent as AccountIcon } from 'resources/graphics/icons/account.svg'
import { ReactComponent as TrinityHorizontalLogo } from 'resources/graphics/trinity_horizontal_logo.svg'
import { ReactComponent as MicrophoneIcon } from 'resources/graphics/icons/microphone.svg'
import { routes } from 'routes/RouterStackComponent'
import { selectCurrentUser } from 'store/services/User'

import './Header.scss'

const Wrapper: React.FC = ({ children }) => <span className="header__badge-wrapper">{children}</span>

const Header: React.FC<RouteComponentProps> = (props) => {
  const [collapsableOpen, setCollapsableOpen] = useState(false)
  const user = useSelector(selectCurrentUser)

  const { pathname } = props.location
  const isBookRoute = pathname.includes(routes.book)
  const isConratulationRoute = pathname.includes(routes.congratulation)
  const isExerciseRoute = pathname.includes(routes.exercises)
  const isProfileRoute = pathname.includes(routes.profile)
  const isPronunciationRoute = pathname.includes(routes.pronunciation)
  const showCloseButton =
    isBookRoute || isConratulationRoute || isProfileRoute || isExerciseRoute || isPronunciationRoute

  const reloadWindow = useCallback(() => {
    window.location.reload()
  }, [])

  const userInfoContent = useMemo(
    () => (
      <>
        <Wrapper>
          <GlobalScore />
        </Wrapper>
        <Wrapper>
          <Level />
        </Wrapper>
        {user?.rewards && (
          <Wrapper>
            <span
              aria-label={`Achievements earned: ${user.rewards.length}`}
              style={{ display: 'flex', alignItems: 'center' }}
            >
              <AchievementIcon className="header__achievement-icon" aria-hidden="true" focusable="false" />
              {user.rewards.length}
            </span>
          </Wrapper>
        )}
        <Wrapper>
          <button
            style={{ padding: 0, display: 'flex', background: 'transparent' }}
            type="button"
            aria-label="Open pronunciation pro"
            onClick={() => {
              setCollapsableOpen(false)
              props.history.push(routes.pronunciation)
            }}
          >
            <MicrophoneIcon className="header__pronunciation-icon" aria-hidden="true" focusable="false" />
          </button>
        </Wrapper>
        <Wrapper>
          <button
            style={{ padding: 0, display: 'flex', background: 'transparent' }}
            type="button"
            aria-label="Open profile"
            onClick={() => {
              setCollapsableOpen(false)
              props.history.push(routes.profile)
            }}
          >
            <AccountIcon className="header__profile-icon" aria-hidden="true" focusable="false" />
          </button>
        </Wrapper>
        <Wrapper>
          <a
            href="https://www.trinitycollege.com/qualifications/english-language/skill-up"
            target="_blank"
            aria-label="Open Trinity College London Skill Up website in a new tab"
            rel="noopener noreferrer"
          >
            <HelpIcon className="header__profile-icon" aria-hidden="true" focusable="false" />
          </a>
        </Wrapper>
      </>
    ),
    // eslint-disable-next-line
    [user]
  )

  return (
    <header className="header" role="banner">
      <div className="header__container">
        {showCloseButton ? (
          <button
            style={{ padding: 0, display: 'flex', background: 'transparent' }}
            type="button"
            aria-label="Close and return to home"
            onClick={() => props.history.replace('/')}
          >
            <CloseIcon className="header__close" aria-hidden="true" focusable="false" />
          </button>
        ) : (
          <button
            style={{ padding: 0, display: 'flex', background: 'transparent' }}
            type="button"
            aria-label="Reload page"
            onClick={reloadWindow}
          >
            <TrinityHorizontalLogo className="header__logo" aria-hidden="true" focusable="false" />
          </button>
        )}
        <div className="header__user-info">{userInfoContent}</div>
        <button
          type="button"
          className="header__icon-button"
          aria-label="Toggle menu"
          aria-expanded={collapsableOpen}
          aria-controls="header-collapsable-menu"
          onClick={() => setCollapsableOpen((val) => !val)}
        >
          <MenuIcon className="header__menu-icon" aria-hidden="true" focusable="false" />
        </button>
      </div>
      <div className="header__progress-bar-container">{isBookRoute && <ExerciseProgressBar />}</div>
      <div id="header-collapsable-menu" className={`collapsable-bar${collapsableOpen ? '--open' : ''}`}>
        {userInfoContent}
      </div>
    </header>
  )
}

export default withRouter(Header)
